import addEnsureEntity from './extensions/ensureEntity'
import { cloudFrontUrlTransform } from './extensions/cloudFrontTransform'
import { GroupType } from './enums'

export default function(store, { host }) {
  const Group = store.defineModel('group', {
    replaceKeys: {
      avatar: 'avatarUrl',
      createdTime: 'createdAt',
      displayName: 'name',
      members: 'memberIds',
      numMembers: '__memberCount',
      organizationKey: 'organizationId',
      proxied_members: 'proxiedMembers',
    },

    transientAttrs: [
      // store memberCount in __memberCount, and turn it into a property (computed) that returns memberIds.length
      // if available, or this number. ROOM groups don't have memberIds array
      '__memberCount',
    ],

    defaultValues: {
      hasCurrentUser: false,
      isPublic: false,
      memberIds: null,
    },

    skipCamelizationForKeys: [
      'escalation',
      'metadata',
      'proxied_members',
    ],

    parseAttrs(attrs) {
      if ('createdAt' in attrs) attrs.createdAt = new Date(attrs.createdAt)
      if ('createdBy' in attrs) delete attrs['createdBy']

      if (attrs.avatarUrl) {
        attrs.avatarUrl = cloudFrontUrlTransform(attrs.avatarUrl, host.config)
      }

      if ('escalation' in attrs) {
        const execution = host.escalations._updateEscalation(attrs.escalation, { inGroupInject: true })
        delete attrs.escalation
        attrs.escalationExecutionId = execution.id
      }

      if (attrs.memberIds) {
        const memberIds = []
        for (const member of attrs.memberIds) {
          if (typeof member === 'string') {
            memberIds.push(member)
          } else if (member.id) {
            memberIds.push(member.id)
          }
        }
        attrs.memberIds = memberIds
      }

      if (attrs.metadata && attrs.metadata['feature_service'] === 'patient_care') {
        attrs.patientDetails = attrs.metadata['patient']
      }

      if (attrs.metadata && attrs.metadata['feature_service'] === 'patient_messaging') {
        attrs.patientDetails = {
          dob: attrs.metadata['patient_dob'],
          gender: attrs.metadata['patient_gender'],
          id: attrs.metadata['patient_id'],
          isPatientContact: attrs.metadata['is_patient_contact'],
          mrn: attrs.metadata['patient_mrn'],
          name: attrs.metadata['patient_name'],
          phoneNumber: attrs.metadata['phone_number'],
        }
      }

      if ('metadata' in attrs) delete attrs['metadata']

      return attrs
    },

    afterAssignment(entity) {
      const { id, memberIds, organizationId } = entity
      let conversation
      if (!host.config.condensedReplays) {
        conversation = organizationId ? host.conversations.get('group', id, organizationId) : null
      } else if (entity.conversationId) {
        conversation = host.conversations.getById(entity.conversationId)
      }
      let metadata = organizationId ? host.metadata.get(id, organizationId) : null
      if (metadata) metadata = metadata.data

      const groupType = host.groups.__extractGroupTypeFromAttrs(entity)
      const displayName = metadata && groupType === GroupType.ROLE_P2P ?
        metadata.target_display_name : entity.name

      entity.conversation = conversation
      entity.displayName = displayName
      entity.groupType = groupType
      entity.memberCount = (memberIds && memberIds.length) || entity.__memberCount || 0
      entity.metadata = metadata

      if (groupType !== 'FORUM') {
        delete entity.hasCurrentUser
      }

      host.users._setMembersOnEntity(entity)
      host.groups._setP2PEntities(entity)
    },

    beforeCreateInstance(attrs) {
      ensureMembersByIds(attrs.id, attrs.memberIds, attrs.organizationId)
    },

    relations: {
      belongsTo: {
        organization: { type: 'organization', foreignKey: 'organizationId' },
        escalationExecution: { type: 'escalationExecution', foreignKey: 'escalationExecutionId' },
      },
    }
  })

  // use onlyPlaceholder because groups may be big and we don't want to pre-load all the users
  // let the app decide when to load the users on demand

  function ensureMembersByIds(id, memberIds, organizationId) {
    if (!memberIds || memberIds.length === 0) return
    host.groups.ensureMembersWithIds(id, memberIds, {
      onlyPlaceholder: true,
      attrs: { organizationId }
    })
  }

  addEnsureEntity(Group, {
    finder: (...args) => host.groups.find(...args),
    placeholderEntityDelayBeforeRefreshing() { return host.config.placeholderEntityDelayBeforeRefreshing },
    placeholderEntityAllowLoading() { return host.config.placeholderEntityAllowLoading },

    defaultPlaceholderAttrs: { displayName: 'Loading group...' },
    defaultNotFoundAttrs: { displayName: '<Group Not Found>' },

    shouldEnsure(entity, requestedOnlyPlaceholder, { groupMembersOnlyPlaceholder }) {
      return (
        !entity.members ||
        !entity.metadata ||
        entity.groupType === GroupType.ROLE_P2P ||
        entity.members.some(m => host.models.User.shouldEnsure(m, groupMembersOnlyPlaceholder))
      )
    },
  })

  return Group
}
